import {
  createUserWithEmailAndPassword,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  onIdTokenChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from 'firebase/auth'
import { doc, getDoc, getFirestore, setDoc } from 'firebase/firestore'
import { updateCurrentUserProfile, useCurrentUser } from 'vuefire'

/**
 * Centralized authentication composable
 *
 * Provides consistent authentication operations with proper subscription management
 * and handles all Firebase auth-related functionality in one place.
 */
export function useAuth() {
  const app = useFirebaseApp()
  const auth = getAuth(app)
  const user = useCurrentUser()
  const {
    registerSubscription,
    clearAllSubscriptions,
  } = useSubscriptions()
  const notificationsStore = useNotificationsStore()
  const membersStore = useMembersStore()
  const subkrewesStore = useSubKrewesStore()
  const tasksStore = useTasksStore()

  /**
   * Create a session for the authenticated user
   */
  const createSession = async (userCredential: { user: User }) => {
    try {
      // For initial login, always force refresh the token
      const idToken = await userCredential.user.getIdToken(true)

      await $fetch('/api/auth/create-session', {
        method: 'POST',
        body: {
          idToken,
          expiresIn: 60 * 60 * 24 * 14 * 1000, // 14 days in milliseconds
        },
      })
    }
    catch (error) {
      console.error('Error creating session:', error)
      throw new Error('Failed to create session')
    }
  }

  /**
   * Create a member document in Firestore
   */
  const createMemberDoc = async (user: User, name: string) => {
    const db = getFirestore()
    const docRef = doc(db, 'members', user.uid)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      // Decode URL-encoded characters (Apple sign-in sometimes encodes spaces as +)
      const decodedName = name.replace(/\+/g, ' ')

      await setDoc(docRef, {
        email: user.email,
        created_at: new Date(),
        updated_at: new Date(),
        member_type: 'pending',
        name: decodedName,
      }).catch((error) => {
        console.error('Error creating member document:', error)
        throw error
      })
    }
  }

  /**
   * Sign in with email and password
   */
  const signInWithEmail = async (email: string, password: string) => {
    try {
      console.warn('[Auth] Attempting to sign in with email')
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      console.warn('[Auth] Sign in successful, user:', userCredential.user.uid)

      // Create session and ensure member document exists
      await createSession(userCredential)
      await createMemberDoc(userCredential.user, userCredential.user.displayName || '')

      return { success: true, user: userCredential.user }
    }
    catch (error) {
      console.error('[Auth] Login error:', error)
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error during login'),
      }
    }
  }

  /**
   * Sign in with Google
   * Uses popup for local development (emulator doesn't support redirect)
   * Uses redirect for production (better mobile experience, avoids popup blockers)
   */
  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider()
      const runtimeConfig = useRuntimeConfig()
      const isEmulator = runtimeConfig.public.krewebacchusEnv === 'dev'

      if (isEmulator) {
        // Use popup for emulator - redirect doesn't work with emulator
        const result = await signInWithPopup(auth, provider)

        if (result?.user) {
          await createSession(result)
          await createMemberDoc(result.user, result.user.displayName || '')
          return { success: true, user: result.user }
        }
        return { success: false, error: new Error('No user returned from Google sign in') }
      }
      else {
        // Use redirect for production - better UX on mobile
        await signInWithRedirect(auth, provider)
        return { success: true, redirecting: true }
      }
    }
    catch (error) {
      console.error('Google login error:', error)
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error during Google login'),
      }
    }
  }

  /**
   * Handle the result from OAuth redirect sign-in (Google or Apple)
   * Call this on app initialization to complete the auth flow after redirect
   */
  const handleOAuthRedirectResult = async () => {
    try {
      const result = await getRedirectResult(auth)

      if (result?.user) {
        console.warn('[Auth] OAuth redirect sign in result')
        // Create session and ensure member document exists
        await createSession(result)
        await createMemberDoc(result.user, result.user.displayName || '')

        return { success: true, user: result.user }
      }
      // No result means user didn't come from a redirect
      return { success: false, noRedirect: true }
    }
    catch (error) {
      console.error('OAuth redirect result error:', error)
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error handling OAuth redirect'),
      }
    }
  }

  // Keep old name for backwards compatibility
  const handleGoogleRedirectResult = handleOAuthRedirectResult

  /**
   * Sign in with Apple
   * Uses popup for local development (emulator doesn't support redirect)
   * Uses redirect for production (better mobile experience)
   */
  const signInWithApple = async () => {
    try {
      const provider = new OAuthProvider('apple.com')
      provider.addScope('email')
      provider.addScope('name')

      const runtimeConfig = useRuntimeConfig()
      const isEmulator = runtimeConfig.public.krewebacchusEnv === 'dev'

      if (isEmulator) {
        // Use popup for emulator - redirect doesn't work with emulator
        const result = await signInWithPopup(auth, provider)

        if (result?.user) {
          await createSession(result)
          await createMemberDoc(result.user, result.user.displayName || '')
          return { success: true, user: result.user }
        }
        return { success: false, error: new Error('No user returned from Apple sign in') }
      }
      else {
        // Use redirect for production - better UX on mobile
        await signInWithRedirect(auth, provider)
        return { success: true, redirecting: true }
      }
    }
    catch (error) {
      console.error('Apple login error:', error)
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error during Apple login'),
      }
    }
  }

  /**
   * Register a new user with email and password
   */
  const registerWithEmail = async (email: string, password: string, fullName: string) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      user.value = userCredential.user

      await updateCurrentUserProfile({
        displayName: fullName,
      })

      // Create session and member document
      await createSession(userCredential)
      await createMemberDoc(userCredential.user, fullName)
      sendEmailVerification(userCredential.user, {
        url: `${window.location.origin}/`,
      })
      return { success: true, user: userCredential.user }
    }
    catch (error) {
      console.error('Registration error:', error)
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error during registration'),
      }
    }
  }

  /**
   * Sign out the current user and clean up subscriptions
   */
  const signOutUser = async () => {
    try {
      // Clean up store subscriptions
      notificationsStore.cleanupSubscriptions()

      // Clean up all tracked subscriptions
      clearAllSubscriptions()

      // Clear session from auth
      const { clearSession } = useAuthSession()

      await clearSession()

      await signOut(auth)

      // Reset user state
      user.value = null

      // Reset store states
      tasksStore.resetState()
      membersStore.$reset()
      subkrewesStore.$reset()
      notificationsStore.$reset()

      return { success: true }
    }
    catch (error) {
      console.error('Logout error:', error)
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error during logout'),
      }
    }
  }

  /**
   * Subscribe to auth state changes
   * Uses onIdTokenChanged instead of onAuthStateChanged to ensure the
   * auth token is ready for Firestore before triggering subscriptions
   */
  const subscribeToAuthChanges = (callback: (user: User | null) => void) => {
    const unsubscribe = onIdTokenChanged(auth, callback)
    registerSubscription(unsubscribe, 'auth')
    return unsubscribe
  }

  /**
   * Load current member data
   */
  const loadCurrentMember = async (): Promise<Member | null> => {
    if (!user.value?.uid) return null
    return await membersStore.fetchMember(user.value.uid)
  }

  /**
   * Check if the current user session is valid
   *
   * This now uses our token caching system to avoid unnecessary API calls
   */
  const checkSession = async () => {
    const { checkSession: checkSessionInternal, refreshSession } = useAuthSession()

    const isValid = await checkSessionInternal()
    if (!isValid) {
      // Try to refresh the session
      return await refreshSession()
    }

    return isValid
  }

  return {
    signInWithEmail,
    signInWithGoogle,
    signInWithApple,
    handleGoogleRedirectResult,
    registerWithEmail,
    signOutUser,
    checkSession,
    subscribeToAuthChanges,
    loadCurrentMember,
    // Expose the current user reactive ref
    user,
    createSession, // Expose createSession for other uses if needed
  }
}
